<script setup lang="tsx">
  import { MailIcon, PencilIcon, TrashIcon } from '@heroicons/vue/solid';
  import { cloneDeep } from 'lodash';
  import CheckboxCollection from '~/components/Admin/Form/CheckboxCollection.vue';
  import ExpenseCategoryCheckbox from '~/components/Admin/Form/ExpenseCategoryCheckbox.vue';
  import { addUserFormDefaultState, createFormStateObject } from '~/components/Admin/Form/UserUtils';
  import HeaderDisplay from '~/components/UI/Drawers/HeaderDisplay.vue';
  import InfoRow from '~/components/UI/Drawers/InfoRow.vue';
  import InfoSection from '~/components/UI/Drawers/InfoSection.vue';
  import { get, post } from '~/services/donationScreen';
  import type { AddUserFormState, GetResponse, GetUserResponse, ResponseStatus, UserInformationDrawer } from '~/types';
  import DeleteUserModal from '../../Modals/DeleteUserModal.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const router = useRouter();
  const { isExpenseEnabled } = useAuth();
  const userInformationResponse = reactive<AddUserFormState>(cloneDeep(addUserFormDefaultState));
  const isEmailVerified = ref<boolean>(false);
  const isDeleteUserModalVisible = ref<boolean>(false);
  const userEmail = ref<string>('');
  const generateStatusStatus = ref<ResponseStatus>('idle');
  const enterpriseVersion = ref<boolean>(false);
  const isSystemAdmin = ref<boolean>(false);

  const openDeleteUserModal = () => {
    isDeleteUserModalVisible.value = true;
  };

  const fetchData = async () => {
    try {
      const response = await get<GetUserResponse>(`v1/get_user/${props.identifier}`);
      const selectedUser = response?.data?.data;
      isEmailVerified.value = selectedUser?.email_verified;
      enterpriseVersion.value = selectedUser.enterprise_version;
      let rolesArray: string[] = [];
      const roleData = selectedUser?.role;

      if (Array.isArray(roleData)) {
        rolesArray = roleData;
      } else if (typeof roleData === 'string' && roleData.length > 0) {
        rolesArray = roleData.split(',').map(r => r.trim());
      } else {
        rolesArray = [];
      }

      isSystemAdmin.value = rolesArray.includes('admin');

      // const customRoles = response?.data?.data?.custom_exp_roles;
      // if (customRoles && typeof customRoles === 'object') {
      //   Object.entries(customRoles).forEach(([key, value]) => {
      //     userInformationResponse.permissions.expenseCategoryRoles.push({
      //       checked: false,
      //       checkboxText: value,
      //       name: key
      //     });
      //   });
      // }
      const groupedRoles: Record<string, any[]> = {};
      Object.entries(response?.data?.data?.custom_exp_roles || {}).forEach(([key, value]) => {
        const baseKey = key
          .replace(/^_+/, '') // normalize
          .replace('_approval_control_admin', '')
          .replace('_admin', '')
          .replace('_approver', '')
          .replace('_submitter', '')
          .trim();

        if (!groupedRoles[baseKey]) {
          groupedRoles[baseKey] = [];
        }

        groupedRoles[baseKey].push({
          checked: false,
          checkboxText: value,
          name: key
        });
      });

      Object.keys(groupedRoles)
        .sort((a, b) => a.localeCompare(b))
        .forEach(baseKey => {
          const group = groupedRoles[baseKey];

          group.sort((a, b) => {
            const order = ['approval_control_admin', 'admin', 'approver', 'submitter'];

            const getType = (name: string) => order.findIndex(type => name.includes(type));

            return getType(a.name) - getType(b.name);
          });

          userInformationResponse.permissions.expenseCategoryRoles.push(...group);
        });

      const formStateObject = createFormStateObject(userInformationResponse, selectedUser);
      Object.assign(userInformationResponse, cloneDeep(formStateObject));
      applyDerivedControlAdmin();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const applyDerivedControlAdmin = () => {
    const permissions = userInformationResponse.permissions;

    Object.values(permissions).forEach(collection => {
      collection.forEach(role => {
        const isModuleAdmin = role.name.endsWith('_admin') && !role.name.includes('approval_control_admin');

        if (isModuleAdmin && role.checked) {
          const base = role.name.replace('_admin', '');
          const controlName = `${base}_approval_control_admin`;

          Object.values(permissions).forEach(col => {
            col.forEach(r => {
              if (r.name === controlName) {
                // ❌ DO NOT auto check
                // Just keep it enabled via computed
              }
            });
          });
        }
      });
    });
  };

  const controlAdminEnabledBases = computed(() => {
    const permissions = userInformationResponse.permissions;
    const result: any = {};

    Object.values(permissions).forEach((collection: any) => {
      collection.forEach((role: any) => {
        const base = role.name
          .replace('_approval_control_admin', '')
          .replace('_admin', '')
          .replace('_approver', '')
          .replace('_submitter', '')
          .replace('_member', '');

        if (!result[base]) {
          result[base] = {
            admin: false,
            other: false
          };
        }

        if (role.name.endsWith('_admin') && !role.name.includes('approval_control_admin')) {
          if (role.checked) result[base].admin = true;
        }

        if (
          (role.name.endsWith('_approver') || role.name.endsWith('_submitter') || role.name.endsWith('_member')) &&
          role.checked
        ) {
          result[base].other = true;
        }
      });
    });

    return result;
  });

  const sendEmailVerification = async () => {
    generateStatusStatus.value = 'pending';
    try {
      const response = await post<GetResponse<UserInformationDrawer>>(
        `v1/users/resend_verification_link/${props.identifier}`
      );
      if (response.data?.status === 'success') {
        openSuccessfulCustomNotification(response?.data?.message);
        generateStatusStatus.value = 'success';
      } else if (response?.data?.status === 'error') {
        generateStatusStatus.value = 'error';
        openFailedCustomNotification(response?.data?.message);
      }
    } catch (err) {
      generateStatusStatus.value = 'error';
      console.error(err);
      openFailedCustomNotification('Failed to send email verification');
    }
  };

  onMounted(() => {
    if (props.identifier) {
      fetchData();
    }
  });

  onMounted(async () => {
    const { identifier } = router.currentRoute.value.query;
    userEmail.value = identifier as string;
  });
  onMounted(() => {
    if (isExpenseEnabled.value) {
      userInformationResponse.permissions.dashboardReportRoles.push(
        {
          checked: false,
          checkboxText: 'Expense Report Manager',
          name: 'expense_report_manager'
        },
        {
          checked: false,
          checkboxText: 'Budget Report Manager',
          name: 'budget_report_manager'
        }
      );
    }
  });
</script>

<template>
  <div v-if="generateStatusStatus === 'pending'" class="loading-overlay">
    <a-spin size="large" />
  </div>
  <a-drawer
    :open="route.query.drawerFrom === 'userInformation'"
    title="User Information"
    width="1100"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-2">
        <a-button
          class="!px-2 !py-1.5 w-fit h-fit flex items-center gap-1 !sm-medium hover:!text-red-600 hover:!border-red-600"
          @click="openDeleteUserModal"
        >
          <TrashIcon class="w-4 h-4" />
          Delete user
        </a-button>
        <a-button
          :disabled="isEmailVerified"
          class="!px-2 !py-1.5 w-fit h-fit flex items-center gap-1 !sm-medium"
          @click="sendEmailVerification"
        >
          <MailIcon class="w-4 h-4" />
          Send Email Verification
        </a-button>
        <nuxt-link :to="`/admin/edit_user?identifier=${props.identifier}`">
          <a-button class="!px-2 !py-1.5 w-fit h-fit flex items-center gap-1 !sm-medium">
            <PencilIcon class="w-4 h-4" />
            Edit User
          </a-button>
        </nuxt-link>
      </div>
    </template>
    <div class="flex flex-col w-full">
      <HeaderDisplay
        :main-display="userInformationResponse.firstName + ' ' + userInformationResponse.lastName"
        :sub-display="userInformationResponse.emailAddress"
      />
      <InfoSection>
        <InfoRow label="Email" :value="userInformationResponse.emailAddress" />
      </InfoSection>
      <InfoSection title="Permissions">
        <div class="grid grid-cols-3 gap-6 px-4">
          <div class="min-w-[320px] flex flex-col gap-3">
            <CheckboxCollection
              title="System Level Roles"
              state-key="systemLevelRoles"
              :checkbox-collection="userInformationResponse.permissions.systemLevelRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :show-only="true"
            />
            <CheckboxCollection
              title="Auditor Roles"
              state-key="auditorRoles"
              :checkbox-collection="userInformationResponse.permissions.auditorRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              title="Dashboard & Report Roles"
              state-key="dashboardReportRoles"
              :checkbox-collection="userInformationResponse.permissions.dashboardReportRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              title="Donor & Donation Roles"
              state-key="donorDonationRoles"
              :checkbox-collection="userInformationResponse.permissions.donorDonationRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              v-if="isExpenseEnabled"
              title="Payee Roles"
              state-key="payeeRoles"
              :checkbox-collection="userInformationResponse.permissions.payeeRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
          </div>
          <div class="min-w-[320px] flex flex-col gap-3">
            <CheckboxCollection
              v-if="isExpenseEnabled"
              title="Budget Roles"
              state-key="budgetRoles"
              :checkbox-collection="userInformationResponse.permissions.budgetRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              title="Grant Roles"
              state-key="grantRoles"
              :checkbox-collection="userInformationResponse.permissions.grantRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              title="Pledges Roles"
              state-key="pledgesRoles"
              :checkbox-collection="userInformationResponse.permissions.pledgesRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              v-if="isExpenseEnabled"
              title="Advance Roles"
              state-key="advanceRoles"
              :checkbox-collection="userInformationResponse.permissions.advanceRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
          </div>
          <div class="min-w-[320px] flex flex-col gap-3">
            <div v-if="isExpenseEnabled">
              <ExpenseCategoryCheckbox
                title="Expense Category Roles"
                state-key="expenseCategoryRoles"
                :checkbox-collection="[...userInformationResponse.permissions.expenseCategoryRoles]"
                :control-admin-enabled-bases="controlAdminEnabledBases"
                :system-admin="isSystemAdmin"
                :show-only="true"
              />
            </div>
            <CheckboxCollection
              title="Utilities Category Roles"
              state-key="utilitiesCategoryRoles"
              :checkbox-collection="userInformationResponse.permissions.utilitiesCategoryRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
            <CheckboxCollection
              v-if="enterpriseVersion"
              title="Tax Invoice Roles"
              state-key="taxInvoiceCategoryRoles"
              :checkbox-collection="userInformationResponse.permissions.taxInvoiceCategoryRoles"
              :control-admin-enabled-bases="controlAdminEnabledBases"
              :system-admin="isSystemAdmin"
              :show-only="true"
            />
          </div>
        </div>
      </InfoSection>
    </div>
  </a-drawer>
  <DeleteUserModal
    :visible="isDeleteUserModalVisible"
    :close="() => (isDeleteUserModalVisible = false)"
    :user-email="userEmail"
  />
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
  .resend-email-wrapper {
    margin-top: 20px;
  }

  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(255, 255, 255, 0.7); /* semi-transparent white */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999; /* high z-index to stay on top */
  }

  .spinner {
    width: 50px;
    height: 50px;
    border: 6px solid #ccc;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
